import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  useTheme,
} from "@mui/material";
import {
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";

const CommentAttachments = ({ attachments = [] }) => {
  const theme = useTheme();
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  if (!attachments || attachments.length === 0) {
    return null;
  }

  const getAttachmentIcon = (type) => {
    switch (type) {
      case "image":
        return <ImageIcon sx={{ fontSize: 16 }} />;
      case "video":
        return <VideoIcon sx={{ fontSize: 16 }} />;
      default:
        return <FileIcon sx={{ fontSize: 16 }} />;
    }
  };

  const getAttachmentColor = (type) => {
    switch (type) {
      case "image":
        return { bg: "#EBF4FF", color: "#4A90E2" };
      case "video":
        return { bg: "#FFF4E6", color: "#F57C00" };
      default:
        return { bg: "#F5F5F5", color: "#666" };
    }
  };

  const getFileName = (attachment) => {
    try {
      const metadata = typeof attachment.metadata === 'string' 
        ? JSON.parse(attachment.metadata) 
        : attachment.metadata;
      return metadata?.originalName || `Attachment`;
    } catch {
      return `Attachment`;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleImageClick = (attachment) => {
    setSelectedImage(attachment);
  };

  const handleVideoClick = (attachment) => {
    setSelectedVideo(attachment);
  };

  const handleDownload = (attachment) => {
    if (attachment.storage_path) {
      const link = document.createElement("a");
      link.href = `${process.env.REACT_APP_CLOUDFRONT_URL}/${attachment.storage_path}`;
      link.download = getFileName(attachment);
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <>
      <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.7rem",
            color: "#FFFFFF",
            fontWeight: 500,
          }}
        >
          Attachments ({attachments.length})
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {attachments.map((attachment) => {
            const colors = getAttachmentColor(attachment.type);
            const fileName = getFileName(attachment);
            const fileSize = formatFileSize(attachment.size_bytes);

            return (
              <Box
                key={attachment.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  p: 1,
                  bgcolor: colors.bg,
                  borderRadius: 1.5,
                  border: `1px solid ${colors.color}20`,
                  cursor: attachment.type === "image" || attachment.type === "video" ? "pointer" : "default",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: colors.bg,
                    borderColor: colors.color,
                    transform: "translateY(-1px)",
                    boxShadow: `0 2px 8px ${colors.color}20`,
                  },
                }}
                onClick={() => {
                  if (attachment.type === "image") {
                    handleImageClick(attachment);
                  } else if (attachment.type === "video") {
                    handleVideoClick(attachment);
                  }
                }}
              >
                <Box sx={{ color: colors.color, display: "flex", alignItems: "center" }}>
                  {getAttachmentIcon(attachment.type)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: "#2C4A68",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fileName}
                  </Typography>
                  {fileSize && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.65rem",
                        color: "#8FA5C0",
                        display: "block",
                      }}
                    >
                      {fileSize}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(attachment);
                  }}
                  sx={{
                    p: 0.5,
                    color: colors.color,
                    "&:hover": {
                      bgcolor: `${colors.color}15`,
                    },
                  }}
                >
                  <DownloadIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Image Preview Dialog */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: "#000",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: "#fff",
            pb: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: "#fff" }}>
            {selectedImage ? getFileName(selectedImage) : ""}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setSelectedImage(null)}
            sx={{ color: "#fff" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: "flex", justifyContent: "center", bgcolor: "#000" }}>
          {selectedImage && selectedImage.storage_path && (
            <img
              src={`${process.env.REACT_APP_CLOUDFRONT_URL}/${selectedImage.storage_path}`}
              alt={getFileName(selectedImage)}
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: "#000", px: 2, pb: 2 }}>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => {
              if (selectedImage) {
                handleDownload(selectedImage);
              }
            }}
            sx={{
              color: "#fff",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            Download
          </Button>
          <Button
            startIcon={<OpenInNewIcon />}
            onClick={() => {
              if (selectedImage?.storage_path) {
                window.open(`${process.env.REACT_APP_CLOUDFRONT_URL}/${selectedImage.storage_path}`, "_blank");
              }
            }}
            sx={{
              color: "#fff",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            Open in New Tab
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video Preview Dialog */}
      <Dialog
        open={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: "#000",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: "#fff",
            pb: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: "#fff" }}>
            {selectedVideo ? getFileName(selectedVideo) : ""}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setSelectedVideo(null)}
            sx={{ color: "#fff" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: "flex", justifyContent: "center", bgcolor: "#000" }}>
          {selectedVideo && selectedVideo.storage_path && (
            <video
              controls
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
              }}
            >
              <source src={`${process.env.REACT_APP_CLOUDFRONT_URL}/${selectedVideo.storage_path}`} type={selectedVideo.mime_type} />
              Your browser does not support the video tag.
            </video>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: "#000", px: 2, pb: 2 }}>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => {
              if (selectedVideo) {
                handleDownload(selectedVideo);
              }
            }}
            sx={{
              color: "#fff",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            Download
          </Button>
          <Button
            startIcon={<OpenInNewIcon />}
            onClick={() => {
              if (selectedVideo?.storage_path) {
                window.open(`${process.env.REACT_APP_CLOUDFRONT_URL}/${selectedVideo.storage_path}`, "_blank");
              }
            }}
            sx={{
              color: "#fff",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            Open in New Tab
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CommentAttachments;
